import { useEffect, useRef, useState } from "react";
import { Button } from "./ui/button";
import { Mic, Square, Pause, Play, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * VoiceRecorder — browser MediaRecorder wrapper for dictation.
 * Props:
 *   onUpload(blob): async (blob) => result — caller uploads and awaits backend response
 *   compact: bool — smaller inline variant for per-section use
 *   disabled: bool
 */
export default function VoiceRecorder({ onUpload, compact = false, disabled }) {
  const [state, setState] = useState("idle"); // idle | recording | paused | uploading
  const [seconds, setSeconds] = useState(0);
  const mediaRecorder = useRef(null);
  const chunks = useRef([]);
  const tick = useRef(null);
  const stream = useRef(null);

  useEffect(() => () => cleanup(), []);

  const cleanup = () => {
    if (tick.current) clearInterval(tick.current);
    if (stream.current) stream.current.getTracks().forEach((t) => t.stop());
    stream.current = null;
    mediaRecorder.current = null;
    chunks.current = [];
  };

  const fmtTime = (s) => {
    const m = Math.floor(s / 60).toString().padStart(2, "0");
    const r = (s % 60).toString().padStart(2, "0");
    return `${m}:${r}`;
  };

  const start = async () => {
    try {
      const s = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.current = s;
      // webm/opus is smallest and well-supported
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus") ? "audio/webm;codecs=opus" : "audio/webm";
      const mr = new MediaRecorder(s, { mimeType: mime, audioBitsPerSecond: 32000 });
      mediaRecorder.current = mr;
      chunks.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunks.current.push(e.data); };
      mr.onstop = async () => {
        const blob = new Blob(chunks.current, { type: "audio/webm" });
        cleanup();
        setSeconds(0);
        setState("uploading");
        try {
          await onUpload(blob);
        } finally {
          setState("idle");
        }
      };
      mr.start(1000); // collect in 1s chunks
      setSeconds(0);
      setState("recording");
      tick.current = setInterval(() => setSeconds((x) => x + 1), 1000);
    } catch (e) {
      toast.error("Microphone access denied. Please enable it and try again.");
    }
  };

  const pause = () => {
    mediaRecorder.current?.pause();
    if (tick.current) clearInterval(tick.current);
    setState("paused");
  };

  const resume = () => {
    mediaRecorder.current?.resume();
    tick.current = setInterval(() => setSeconds((x) => x + 1), 1000);
    setState("recording");
  };

  const stop = () => {
    mediaRecorder.current?.stop();
  };

  if (state === "uploading") {
    return (
      <div className={`flex items-center gap-2 text-sm text-navy/80 bg-gold/10 border border-gold/40 ${compact ? "px-3 py-1.5 text-xs" : "px-4 py-3"}`}>
        <Loader2 className={`animate-spin text-gold ${compact ? "w-3.5 h-3.5" : "w-4 h-4"}`} />
        <span>{compact ? "Transcribing..." : "Transcribing & filling form — this takes ~30–60 seconds..."}</span>
      </div>
    );
  }

  if (state === "idle") {
    if (compact) {
      return (
        <Button
          onClick={start}
          disabled={disabled}
          size="sm"
          variant="outline"
          data-testid="voice-start-btn-compact"
          className="rounded-none border-navy/20 text-navy hover:bg-navy hover:text-ivory"
        >
          <Mic className="w-3.5 h-3.5 mr-1.5" /> Dictate section
        </Button>
      );
    }
    return (
      <Button
        onClick={start}
        disabled={disabled}
        data-testid="voice-start-btn"
        className="bg-navy hover:bg-navy/90 text-ivory rounded-none"
      >
        <Mic className="w-4 h-4 mr-2" /> Record Interview to Auto-Fill
      </Button>
    );
  }

  return (
    <div className={`flex items-center gap-2 bg-white border-2 border-gold ${compact ? "px-2.5 py-1.5" : "px-4 py-3 gap-3"}`}>
      <span className={`relative flex ${compact ? "h-2 w-2" : "h-3 w-3"}`} aria-hidden="true">
        {state === "recording" && (
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-gold opacity-75" />
        )}
        <span className={`relative inline-flex rounded-full ${compact ? "h-2 w-2" : "h-3 w-3"} ${state === "recording" ? "bg-gold" : "bg-navy/30"}`} />
      </span>
      <span className={`font-mono text-navy font-medium ${compact ? "text-xs" : ""}`} data-testid="voice-timer">{fmtTime(seconds)}</span>
      {!compact && (
        <>
          <span className="text-xs text-navy/50 uppercase tracking-widest ml-1">
            {state === "recording" ? "Recording" : "Paused"}
          </span>
          <div className="flex-1" />
        </>
      )}
      {state === "recording" ? (
        <Button size="sm" variant="outline" onClick={pause} data-testid="voice-pause-btn" className="rounded-none border-navy/20 h-7">
          <Pause className={compact ? "w-3 h-3" : "w-3.5 h-3.5 mr-1"} /> {!compact && "Pause"}
        </Button>
      ) : (
        <Button size="sm" variant="outline" onClick={resume} data-testid="voice-resume-btn" className="rounded-none border-navy/20 h-7">
          <Play className={compact ? "w-3 h-3" : "w-3.5 h-3.5 mr-1"} /> {!compact && "Resume"}
        </Button>
      )}
      <Button size="sm" onClick={stop} data-testid="voice-stop-btn" className="bg-gold hover:bg-gold/90 text-navy rounded-none font-semibold h-7">
        <Square className={compact ? "w-3 h-3" : "w-3.5 h-3.5 mr-1"} /> {!compact && "Stop & Transcribe"}
      </Button>
    </div>
  );
}
